import type { WorkflowAttachment } from '../../../blocksuite/ai/components/ai-chat-content/type';
import type { SubgraphContext } from './graph-types';
import { resolveNodeTypeSlugFromRawNode } from './node-type-slug-resolver';
import type { AdaptedWorkflow } from './workflow-adapter';

export const WORKFLOW_CHAT_CONTEXT_EVENT =
  'streamlain:workflow-chat-context-changed';

export interface WorkflowChatContextEventDetail {
  workspaceId: string;
  attachment?: WorkflowAttachment;
  clearContextKey?: string;
  openChat?: boolean;
  force?: boolean;
}

const activeWorkflowChatContexts = new Map<
  string,
  WorkflowChatContextEventDetail
>();

export function getActiveWorkflowChatContext(
  workspaceId: string
): WorkflowChatContextEventDetail | null {
  return activeWorkflowChatContexts.get(workspaceId) ?? null;
}

interface BuildWorkflowChatAttachmentInput {
  workflowId: string;
  workflowName: string;
  graphData: AdaptedWorkflow;
  selectedVersion: number | null;
  publishedVersion: number | null;
  draftVersion: number | null;
  isDirty: boolean;
  baseSnapshotVersion?: number | null;
  subgraph?: SubgraphContext | null;
}

function resolveVersionContext(
  input: Pick<
    BuildWorkflowChatAttachmentInput,
    'selectedVersion' | 'publishedVersion' | 'draftVersion' | 'isDirty'
  >
): Pick<
  WorkflowAttachment,
  'version' | 'versionKey' | 'versionRole' | 'access' | 'workingCopyRevision'
> {
  if (input.selectedVersion !== null) {
    const versionRole =
      input.selectedVersion === input.publishedVersion
        ? ('published' as const)
        : ('snapshot' as const);
    return {
      version: input.selectedVersion,
      versionKey: `${versionRole}:${input.selectedVersion}`,
      versionRole,
      access: 'read-only',
      workingCopyRevision: null,
    };
  }

  if (
    input.isDirty ||
    input.draftVersion !== null ||
    input.publishedVersion === null
  ) {
    return {
      version: input.draftVersion,
      versionKey: `draft:${input.draftVersion ?? 'working-copy'}`,
      versionRole: 'draft',
      access: 'editable',
      workingCopyRevision:
        input.draftVersion === null ? null : `snapshot:${input.draftVersion}`,
    };
  }

  return {
    version: input.publishedVersion,
    versionKey: `published:${input.publishedVersion}`,
    versionRole: 'published',
    access: 'read-only',
    workingCopyRevision: null,
  };
}

function wholeWorkflowSubgraph(graphData: AdaptedWorkflow): SubgraphContext {
  const nodeIdToName = new Map(
    graphData.nodes.map(node => [node.id, node.label])
  );
  const selectedNodes = graphData.rawNodes.map(node => ({
    name: node.name,
    type: node.type,
    slug: resolveNodeTypeSlugFromRawNode(node),
    position: node.position,
    parameters: node.parameters ?? {},
    credentials: undefined,
    disabled: node.disabled,
  }));
  const edges = graphData.edges.map(edge => ({
    source: nodeIdToName.get(edge.source) ?? edge.source,
    sourceOutput: edge.sourceOutput,
    target: nodeIdToName.get(edge.target) ?? edge.target,
    targetInput: edge.targetInput,
  }));
  const serializedLength = JSON.stringify({ selectedNodes, edges }).length;
  return {
    selectedNodes,
    haloNodes: [],
    edges,
    tokenEstimate: Math.ceil(serializedLength / 4),
  };
}

export function buildWorkflowChatAttachment(
  input: BuildWorkflowChatAttachmentInput
): WorkflowAttachment {
  const versionContext = resolveVersionContext(input);
  const subgraph = input.subgraph ?? wholeWorkflowSubgraph(input.graphData);
  const scope = input.subgraph ? 'selected-subgraph' : 'whole-workflow';
  const contextKey = `${input.workflowId}:${versionContext.versionKey}`;

  return {
    workflowId: input.workflowId,
    workflowName: input.workflowName,
    ...versionContext,
    baseSnapshotVersion: input.baseSnapshotVersion ?? null,
    scope,
    contextKey,
    autoSelected: true,
    selectedNodes: subgraph.selectedNodes,
    haloNodes: subgraph.haloNodes,
    edges: subgraph.edges,
    tokenEstimate: subgraph.tokenEstimate,
    source: 'fullscreen',
    priority: 'highest',
  };
}

export function dispatchWorkflowChatContext(
  detail: WorkflowChatContextEventDetail
): void {
  if (detail.attachment) {
    activeWorkflowChatContexts.set(detail.workspaceId, detail);
  } else if (detail.clearContextKey) {
    const active = activeWorkflowChatContexts.get(detail.workspaceId);
    if (active?.attachment?.contextKey === detail.clearContextKey) {
      activeWorkflowChatContexts.delete(detail.workspaceId);
    }
  }
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<WorkflowChatContextEventDetail>(
      WORKFLOW_CHAT_CONTEXT_EVENT,
      { detail }
    )
  );
}
